import { readHomeCountryChoice, resolveIpHomeCountry } from '@peekapak/country-origin';
import {
  backendRegionForResidency,
  residencyForHomeCountry,
  servicesForBackendRegion,
  type BackendRegion,
  type DataResidency,
} from '@peekapak/common/lib/regionAndResidency';

export enum SupportedDataResidency {
  USA = 'USA',
  ARE = 'ARE',
}

// The SupportedDataResidency enum values are exactly the canonical
// DataResidency strings; bridge the nominal enum type to the canonical union.
function asCanonicalResidency(residency: SupportedDataResidency): DataResidency {
  return residency as unknown as DataResidency;
}

export interface DataResidencyConfig {
  dataResidency: SupportedDataResidency;
  cognitoAwsRegion: string;
  dynamoDbAwsRegion: string;
  lambdaAwsRegion: string;
  apiEndpointSuffix: string;
}

// Per-service AWS regions come from the single source of truth
// (regionAndResidency); only the frontend-specific apiEndpointSuffix is local.
const usaServices = servicesForBackendRegion('us-east-1');
const areServices = servicesForBackendRegion('eu-central-1');

export const DATA_RESIDENCY_CONFIGS: Record<SupportedDataResidency, DataResidencyConfig> = {
  [SupportedDataResidency.USA]: {
    dataResidency: SupportedDataResidency.USA,
    cognitoAwsRegion: usaServices.cognito,
    dynamoDbAwsRegion: usaServices.dynamodb,
    lambdaAwsRegion: usaServices.compute,
    apiEndpointSuffix: '',
  },
  [SupportedDataResidency.ARE]: {
    dataResidency: SupportedDataResidency.ARE,
    cognitoAwsRegion: areServices.cognito,
    dynamoDbAwsRegion: areServices.dynamodb,
    lambdaAwsRegion: areServices.compute,
    apiEndpointSuffix: '-fra',
  },
};

// Resolves a stored data residency value, migrating legacy lowercase values
// ('us'/'uae') to their ISO 3166-1 alpha-3 equivalents ('USA'/'ARE').
function resolveStoredDataResidency(storedValue: string | null): SupportedDataResidency | null {
  if (storedValue === 'us') {
    return SupportedDataResidency.USA;
  }
  if (storedValue === 'uae') {
    return SupportedDataResidency.ARE;
  }
  if (storedValue && Object.values(SupportedDataResidency).includes(storedValue as SupportedDataResidency)) {
    return storedValue as SupportedDataResidency;
  }
  return null;
}

export class DataResidencyManager {
  private static instance: DataResidencyManager;
  private currentDataResidency: SupportedDataResidency = SupportedDataResidency.USA;
  private readonly changeListeners = new Set<() => void>();

  private constructor() {}

  public static getInstance(): DataResidencyManager {
    if (!DataResidencyManager.instance) {
      DataResidencyManager.instance = new DataResidencyManager();
    }
    return DataResidencyManager.instance;
  }

  /**
   * Observe residency changes, for UI that must re-render when the region the
   * session routes to changes - e.g. the home-country control switching the
   * visitor to the eu-central-1 backend, which has no federated sign-in.
   *
   * Deliberately NOT the `userDataResidencyChanged` window event: that one is
   * handled by DataResidencyAwareAuthHelpers, which responds by globally
   * signing the user out of Cognito. Merely picking a home country must not do
   * that, so change notification for observers is kept separate from it.
   *
   * Returns an unsubscribe function.
   */
  public subscribeToChanges(listener: () => void): () => void {
    this.changeListeners.add(listener);
    return () => {
      this.changeListeners.delete(listener);
    };
  }

  private notifyChangeListeners(): void {
    for (const listener of this.changeListeners) {
      try {
        listener();
      } catch (error) {
        // One bad listener must not stop the rest, nor fail the residency change.
        console.warn('A data residency change listener threw:', error);
      }
    }
  }

  /**
   * Set the session's residency WITHOUT persisting. For detected/guessed
   * signals (IP guess at init, the auth pages' resolver output, error
   * fallbacks): guesses are session state, recomputed each visit, and must
   * never gain the authority of a stored value.
   *
   * On a value CHANGE this also refreshes BackendInterface's endpoint roots
   * (mirroring mypeekaville-web's manager): consumers like
   * `createTeacherAccount` read module-level root snapshots, so without the
   * refresh a residency change steers the X-Backend-Region header but keeps
   * sending requests to the previous region's host. Dynamic import, because
   * BackendInterface statically imports this module.
   *
   * Returns when that refresh has completed (it never rejects), so callers
   * that fire a request immediately after - initialization above all - can
   * await routing consistency. Fire-and-forget callers may ignore it.
   */
  public setDataResidency(dataResidency: SupportedDataResidency): Promise<void> {
    const previousDataResidency = this.currentDataResidency;
    this.currentDataResidency = dataResidency;

    if (previousDataResidency !== dataResidency) {
      // On a microtask, not inline: getCurrentDataResidency() can migrate a
      // legacy stored value (and so land here) while a component is reading it
      // during render, and notifying synchronously would set state mid-render.
      void Promise.resolve().then(() => this.notifyChangeListeners());

      return import('./BackendInterface')
        .then(({ refreshEndpoints }) => refreshEndpoints())
        .catch((error) => {
          console.warn('Could not refresh endpoints after data residency change:', error);
        });
    }
    return Promise.resolve();
  }

  /**
   * Set AND persist the residency. Only for signals with authority: the
   * user's explicit home-country choice, or the account's real region
   * learned at successful sign-in/sign-up/QR/OAuth. The stored value wins
   * over any in-session guess on every read.
   */
  public persistDataResidency(dataResidency: SupportedDataResidency): Promise<void> {
    const endpointsRefreshed = this.setDataResidency(dataResidency);
    localStorage.setItem('peekapak.userDataResidency', dataResidency);
    return endpointsRefreshed;
  }

  /** Drop the stored residency (e.g. the user cleared their explicit choice). */
  public clearPersistedDataResidency(): void {
    localStorage.removeItem('peekapak.userDataResidency');
  }

  /**
   * The stored (authoritative) residency, or null when nothing has been
   * persisted. Unlike getCurrentDataResidency this never falls back to the
   * in-memory session guess, so callers can distinguish "known" from
   * "guessed".
   */
  public getPersistedDataResidency(): SupportedDataResidency | null {
    return resolveStoredDataResidency(localStorage.getItem('peekapak.userDataResidency'));
  }

  public getCurrentDataResidency(): SupportedDataResidency {
    // Check localStorage first
    const storedDataResidency = localStorage.getItem('peekapak.userDataResidency');
    const resolvedDataResidency = resolveStoredDataResidency(storedDataResidency);
    if (resolvedDataResidency) {
      if (resolvedDataResidency !== storedDataResidency) {
        // Persist migrated legacy value
        this.persistDataResidency(resolvedDataResidency);
      } else {
        this.currentDataResidency = resolvedDataResidency;
      }
    }

    return this.currentDataResidency;
  }

  public getCurrentConfig(): DataResidencyConfig {
    return DATA_RESIDENCY_CONFIGS[this.getCurrentDataResidency()];
  }

  /**
   * The backend region (AWS region string) that serves the current residency.
   * Sent as the `X-Backend-Region` request header so the backend can route
   * without re-deriving the region.
   */
  public getBackendRegion(): BackendRegion {
    return backendRegionForResidency(asCanonicalResidency(this.getCurrentDataResidency()));
  }

  public isAREDataResidency(): boolean {
    return this.getCurrentDataResidency() === SupportedDataResidency.ARE;
  }

  public getApiSubdomain(): string {
    const config = this.getCurrentConfig();
    return config.apiEndpointSuffix === '-fra' ? 'api-fra' : 'api';
  }

  public getCognitoConfiguration(dataResidency?: SupportedDataResidency) {
    // Use provided data residency or fall back to current
    const targetDataResidency = dataResidency || this.getCurrentDataResidency();

    if (targetDataResidency === SupportedDataResidency.ARE) {
      if (import.meta.env.MODE === 'development') {
        return import('@peekapak/common/configuration/devCognitoConfigurationFRA.json');
      } else {
        return import('@peekapak/common/configuration/prodCognitoConfigurationFRA.json');
      }
    } else {
      if (import.meta.env.MODE === 'development') {
        return import('@peekapak/common/configuration/devCognitoConfiguration.json');
      } else {
        return import('@peekapak/common/configuration/prodCognitoConfiguration.json');
      }
    }
  }

  public async detectUserDataResidencyFromIP(): Promise<SupportedDataResidency> {
    // Delegate to the single IP-signal source (the unified country-of-origin
    // resolver) and derive residency through the canonical
    // `residencyForHomeCountry` mapping. This replaces the former local
    // `areCountryCodes` list (which also read the wrong `countryCode` field, so
    // ARE was never detected from IP); unknown/failed lookups fall back to `UNK`,
    // which the canonical mapping derives to the default `USA`.
    const ipHomeCountry = await resolveIpHomeCountry();
    return residencyForHomeCountry(ipHomeCountry ?? 'UNK') as SupportedDataResidency;
  }

  public async initializeDataResidency(): Promise<void> {
    // Highest precedence (D2): an explicit home-country choice the user
    // persisted via the flag control. It derives residency directly with no
    // network and wins over any stored residency or IP guess. Read here at init
    // WITHOUT the browser-geolocation path, so cold marketing pages never prompt
    // or fire geo-places (D14).
    const explicitChoice = readHomeCountryChoice();
    if (explicitChoice) {
      // User-asserted: persisted so the stored tier reflects the choice.
      // Awaited (as below) so init only resolves once the endpoint roots
      // match the residency - a request fired right after init must not hit
      // the previous region's host.
      await this.persistDataResidency(residencyForHomeCountry(explicitChoice) as SupportedDataResidency);
      return;
    }

    // Next: a residency already stored from a prior authoritative event
    // (explicit choice or a successful sign-in/sign-up). Sync in-memory only.
    const storedDataResidency = resolveStoredDataResidency(localStorage.getItem('peekapak.userDataResidency'));
    if (storedDataResidency) {
      await this.setDataResidency(storedDataResidency);
      return;
    }

    // Otherwise detect from the IP guess (geo-places-free; no prompt at init).
    // A guess is session state: NOT persisted, so detection runs again next
    // visit and a later, better signal (geolocation, choice, login) is never
    // blocked by a stored guess.
    const detectedDataResidency = await this.detectUserDataResidencyFromIP();
    await this.setDataResidency(detectedDataResidency);
  }
}

export const dataResidencyManager = DataResidencyManager.getInstance();
